import type { CartLine } from "@memeup/shared";
import { pixel } from "./pixel.ts";
import { createStore } from "./store.ts";

/** Display data cached with each line so the bag renders instantly; prices are re-checked by the server. */
export interface CartItem {
  line: CartLine;
  title: string;
  variantTitle: string;
  imageUrl: string | null;
  unitPriceCents: number;
  href: string;
}

const cart = createStore<{ items: CartItem[] }>({ items: [] }, "memeup.cart.v1");

export const keyOf = (l: CartLine) =>
  l.kind === "catalog" ? `c:${l.variantId}` : `d:${l.designId}:${l.baseVariantId}`;

export const cartStore = {
  ...cart,
  add(item: CartItem) {
    pixel.addToCart(keyOf(item.line), item.title, item.unitPriceCents * item.line.quantity);
    cart.set((s) => {
      const k = keyOf(item.line);
      const existing = s.items.find((i) => keyOf(i.line) === k);
      if (existing) {
        return {
          items: s.items.map((i) =>
            keyOf(i.line) === k
              ? {
                  ...i,
                  line: { ...i.line, quantity: Math.min(20, i.line.quantity + item.line.quantity) },
                }
              : i,
          ),
        };
      }
      return { items: [...s.items, item] };
    });
  },
  setQuantity(index: number, quantity: number) {
    cart.set((s) => ({
      items: s.items.map((i, n) =>
        n === index
          ? { ...i, line: { ...i.line, quantity: Math.max(1, Math.min(20, quantity)) } }
          : i,
      ),
    }));
  },
  remove(index: number) {
    cart.set((s) => ({ items: s.items.filter((_, n) => n !== index) }));
  },
  clear() {
    cart.set({ items: [] });
  },
};

export const useCartCount = () =>
  cartStore.use((s) => s.items.reduce((n, i) => n + i.line.quantity, 0));
export const useCartItems = () => cartStore.use((s) => s.items);
